import { useCallback, useEffect, useState } from 'react';
import { BranchGroupsAPI } from '../../../api/BranchGroups';
import { EventsFetchAPI } from '../../../api/Events';

/* Catálogos de los filtros globales (grupos de sucursales y eventos).
   Cada uno expone { items, loading, error, loaded, reload }. Un error no bloquea el Overview: el filtro muestra el
   problema y permite reintentar, y mientras no se haya cargado bien no se descarta ninguna selección guardada. */

const readStorageJSON = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const errorMessage = (error, fallback) => error?.response?.data?.error || error?.response?.data?.message || fallback;

function useCatalog(enabled, key, fetchItems, fallbackMessage) {
  const [state, setState] = useState({ items: [], loading: Boolean(enabled), error: null, loaded: false });
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((value) => value + 1), []);

  useEffect(() => {
    if (!enabled) {
      setState({ items: [], loading: false, error: null, loaded: false });
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    fetchItems()
      .then((items) => {
        if (!cancelled) setState({ items, loading: false, error: null, loaded: true });
      })
      .catch((error) => {
        if (!cancelled) setState((prev) => ({ ...prev, loading: false, error: errorMessage(error, fallbackMessage), loaded: false }));
      });

    return () => { cancelled = true; };
    // fetchItems depends only on `key` (the company); the rest is intentionally excluded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key, version]);

  return { ...state, reload };
}

// Grupos activos de la empresa (el servidor ya limita los miembros a las sucursales que el usuario puede ver)
export function useBranchGroups(businessCode) {
  return useCatalog(
    Boolean(businessCode),
    businessCode,
    async () => {
      const response = await BranchGroupsAPI.getBranchGroups({
        business_code: businessCode,
        status: 'active',
        token: localStorage.getItem('x-access-token'),
      });
      return Array.isArray(response?.data?.data) ? response.data.data : [];
    },
    'No se pudieron cargar los grupos'
  );
}

// Eventos de la empresa con sus fechas, reutilizando GET /event del módulo de Eventos
export function useEventsCatalog(businessCode) {
  const businessId = readStorageJSON('business')?.id ?? readStorageJSON('user')?.business_id;

  return useCatalog(
    Boolean(businessCode && businessId),
    `${businessCode}:${businessId}`,
    async () => {
      const response = await EventsFetchAPI.getEventsByBusiness(businessId, localStorage.getItem('x-access-token'));
      const events = Array.isArray(response?.data?.events) ? response.data.events : [];
      return events.map((event) => ({
        id: event.id,
        name: event.name,
        dates: Array.isArray(event.dates) ? event.dates : [],
      }));
    },
    'No se pudieron cargar los eventos'
  );
}
